/**
 * Content Fetcher - Background content download for offline use
 *
 * When enabled, this module fetches article content in the background
 * after the initial metadata sync completes. This ensures users have
 * full offline access to their articles while keeping the initial load fast.
 *
 * Features:
 * - Respects autoFetchContent setting
 * - Prioritizes starred articles, then recent, then oldest
 * - Rate-limited to avoid overwhelming the server
 * - Pauses during article reading (content needed immediately)
 * - Resumes automatically when idle
 */

import { syncState, observe } from '@legendapp/state';
import { getArticlesWithoutContent, loadArticleContentBatch, articles$, initCachedIds } from '../store/articles$';
import { settings$ } from '../store/settings$';

// Configuration
const BATCH_SIZE = 3; // Articles per batch
const BATCH_DELAY_MS = 10000; // Delay between batches (10 seconds)
const INITIAL_DELAY_MS = 5000; // Wait before first fetch (5 seconds)

// State
let isRunning = false;
let isPaused = false;
let fetchTimeoutId = null;
let syncObserverDispose = null;
let articlesObserverDispose = null; // Observer for detecting new articles when idle
let fetchAbortController = null; // Cancels in-flight Supabase requests on stop

/**
 * Initialize content fetcher to start when articles$ persistence loads
 * Uses Legend State's syncState to observe when isPersistLoaded becomes true
 * (isPersistLoaded = IndexedDB cache loaded, much faster than waiting for remote sync)
 * @returns {function} Cleanup function to dispose the observer
 */
export const initContentFetcherOnSync = () => {
  // Clean up any existing observer
  if (syncObserverDispose) {
    syncObserverDispose();
    syncObserverDispose = null;
  }

  // Get the sync state observable for articles$
  const articlesState$ = syncState(articles$);

  // Check if persistence is already loaded (e.g., from IndexedDB cache)
  if (articlesState$.isPersistLoaded?.get()) {
    // console.log('[ContentFetcher] Persistence already loaded, starting immediately');
    startContentFetcher();
    return () => stopContentFetcher();
  }

  // Observe the isPersistLoaded state and start fetcher when it becomes true
  // console.log('[ContentFetcher] Waiting for articles$ persistence to load...');
  let hasStarted = false;
  syncObserverDispose = observe(() => {
    const isLoaded = articlesState$.isPersistLoaded?.get();
    if (isLoaded && !hasStarted) {
      hasStarted = true;
      // console.log('[ContentFetcher] Persistence loaded, starting content fetcher');
      startContentFetcher();
      // Stop observing after first trigger to prevent memory leak
      if (syncObserverDispose) {
        syncObserverDispose();
        syncObserverDispose = null;
      }
    }
  });

  // Return cleanup function
  return () => {
    if (syncObserverDispose) {
      syncObserverDispose();
      syncObserverDispose = null;
    }
    stopContentFetcher();
  };
};

/**
 * Start the background content fetcher
 * Should be called after initial metadata sync completes
 */
export const startContentFetcher = () => {
  if (isRunning) {
    // console.log('[ContentFetcher] Already running');
    return;
  }

  // Check if auto-fetch is enabled
  const autoFetch = settings$.autoFetchContent.get();
  if (!autoFetch) {
    // console.log('[ContentFetcher] Auto-fetch disabled, not starting');
    return;
  }

  isRunning = true;
  isPaused = false;
  fetchAbortController = new AbortController();
  // console.log('[ContentFetcher] Starting background content fetch');

  // Initialize content cache ID set before first fetch
  // This populates the in-memory Set used by hasCachedContentSync()
  initCachedIds().then(() => {
    // Delay first fetch to let the app settle after load
    fetchTimeoutId = setTimeout(() => {
      if (isRunning && !isPaused) {
        scheduleFetchBatch();
      }
    }, INITIAL_DELAY_MS);
  });
};

/**
 * Stop the background content fetcher
 */
export const stopContentFetcher = () => {
  // Clean up sync observer if exists
  if (syncObserverDispose) {
    syncObserverDispose();
    syncObserverDispose = null;
  }

  // Clean up articles observer if exists
  if (articlesObserverDispose) {
    articlesObserverDispose();
    articlesObserverDispose = null;
  }

  if (!isRunning) return;

  isRunning = false;
  isPaused = false;

  if (fetchTimeoutId) {
    clearTimeout(fetchTimeoutId);
    fetchTimeoutId = null;
  }

  // Cancel any in-flight Supabase requests
  if (fetchAbortController) {
    fetchAbortController.abort();
    fetchAbortController = null;
  }

  // console.log('[ContentFetcher] Stopped');
};

/**
 * Pause fetching temporarily (e.g., when user opens an article)
 */
export const pauseContentFetcher = () => {
  if (!isRunning || isPaused) return;

  isPaused = true;

  if (fetchTimeoutId) {
    clearTimeout(fetchTimeoutId);
    fetchTimeoutId = null;
  }

  // Also pause the idle observer
  if (articlesObserverDispose) {
    articlesObserverDispose();
    articlesObserverDispose = null;
  }

  // console.log('[ContentFetcher] Paused');
};

/**
 * Resume fetching after pause
 */
export const resumeContentFetcher = () => {
  if (!isRunning || !isPaused) return;

  isPaused = false;
  // console.log('[ContentFetcher] Resumed');

  // Continue fetching
  scheduleFetchBatch();
};

/**
 * Schedule the next batch fetch
 */
const scheduleFetchBatch = () => {
  if (!isRunning || isPaused) return;

  // Check if auto-fetch is still enabled
  const autoFetch = settings$.autoFetchContent.get();
  if (!autoFetch) {
    // console.log('[ContentFetcher] Auto-fetch disabled, stopping');
    stopContentFetcher();
    return;
  }

  fetchTimeoutId = setTimeout(fetchNextBatch, BATCH_DELAY_MS);
};

/**
 * Fetch the next batch of articles without content
 */
const fetchNextBatch = async () => {
  if (!isRunning || isPaused) return;

  // Get articles without content, prioritizing starred articles
  const articleIds = getArticlesWithoutContent({
    limit: BATCH_SIZE,
    priority: 'starred',
  });

  if (articleIds.length === 0) {
    // console.log('[ContentFetcher] All content loaded, entering idle mode');
    // No more articles to fetch - start observing for new articles reactively
    startIdleObserver();
    return;
  }

  // console.log(`[ContentFetcher] Fetching batch of ${articleIds.length} articles`);

  try {
    const signal = fetchAbortController?.signal;
    await loadArticleContentBatch(articleIds, { signal });
    // console.log(`[ContentFetcher] Loaded ${loadedCount}/${articleIds.length} articles`);
  } catch (err) {
    // Silently ignore abort errors (expected during logout/update)
    if (err?.name === 'AbortError') return;
    console.error('[ContentFetcher] Batch fetch error:', err);
  }

  // Schedule next batch (guard against post-stop scheduling)
  if (isRunning && !isPaused) {
    scheduleFetchBatch();
  }
};

/**
 * Start observing articles$ for new articles without content
 * Uses Legend State's reactive observe() instead of polling
 */
const startIdleObserver = () => {
  if (!isRunning || isPaused) return;

  // Clean up any existing observer
  if (articlesObserverDispose) {
    articlesObserverDispose();
    articlesObserverDispose = null;
  }

  // console.log('[ContentFetcher] Starting idle observer for new articles');

  // Flag to prevent race condition when observer callback modifies itself
  let triggered = false;

  // Observe articles$ - this re-runs whenever articles$ changes
  articlesObserverDispose = observe(() => {
    // Skip if already triggered (prevents race condition)
    if (triggered) return;

    // This call to getArticlesWithoutContent internally calls articles$.get()
    // which registers this observer to re-run when articles$ changes
    const articleIds = getArticlesWithoutContent({ limit: 1 });

    if (articleIds.length > 0 && isRunning && !isPaused) {
      triggered = true;
      // console.log('[ContentFetcher] New articles detected, resuming fetch');
      // Defer disposal to next microtask to avoid self-modification during callback
      queueMicrotask(() => {
        if (articlesObserverDispose) {
          articlesObserverDispose();
          articlesObserverDispose = null;
        }
      });
      // Resume batch fetching
      scheduleFetchBatch();
    }
  });
};

/**
 * Check if fetcher is currently running
 */
export const isContentFetcherRunning = () => isRunning;

/**
 * Check if fetcher is paused
 */
export const isContentFetcherPaused = () => isPaused;

/**
 * Get the count of articles without content
 * Useful for displaying progress to user
 */
export const getContentFetchProgress = () => {
  const remaining = getArticlesWithoutContent({ limit: 10000 }).length;
  return { remaining };
};
